import React, { forwardRef, useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Skeleton, useTheme } from '@mui/material';
import { HotelFareCard } from '../Fares/HotelFareCard';
import { HotelFareFinalDto } from '@bandido/shared/dtos';

interface HotelFaresListProps {
  fares: HotelFareFinalDto[];
  onSelect?: (fare: HotelFareFinalDto) => void;
  loading?: boolean;
  quotationStatus: {
    inProgress: boolean;
    fareId: number;
  };
}

const ScrollContainer = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ children, ...props }, ref) => {
    useEffect(() => {
      const styleId = 'hotel-fares-scrollbar-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .hotel-fares-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .hotel-fares-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .hotel-fares-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(74, 20, 140, 0.5);
            border-radius: 4px;
            transition: background-color 0.2s ease;
          }
          .hotel-fares-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(74, 20, 140, 0.8);
          }
          .hotel-fares-scrollbar::-webkit-scrollbar-thumb:active {
            background: rgba(74, 20, 140, 1);
          }
        `;
        document.head.appendChild(style);
      }
    }, []);

    return (
      <div
        ref={ref}
        {...props}
        className="hotel-fares-scrollbar"
        style={{
          ...props.style,
          scrollbarWidth: 'thin' as const,
          scrollbarColor: 'rgba(74, 20, 140, 0.5) transparent',
        }}
      >
        {children}
      </div>
    );
  }
);

export const HotelFaresList: React.FC<HotelFaresListProps> = ({
  fares,
  loading = false,
  onSelect,
  quotationStatus,
}) => {
  const itemCount = loading ? 5 : fares.length;
  const theme = useTheme();
  const [calculatedHeight, setCalculatedHeight] = useState<number>(600);

  useEffect(() => {
    const calculateHeight = () => {
      // no quiero optimizar mucho el tema responsive
      // hasta que no decidamos que hacer con el header y los layouts responsive.
      // por ahora, el offset es 140px, que es el height del header.
      const viewportHeight = window.innerHeight;
      const offsetHeight = 140; 
      const newHeight = viewportHeight - offsetHeight;
      setCalculatedHeight(Math.max(300, newHeight)); 
    };
    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);

    calculateHeight();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderItem = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (loading) {
      return (
        <div style={{...style, padding: theme.spacing(0.5), boxSizing: 'border-box'}}>
          <div style={{ height: '276px' }}>
            <Skeleton height="100%" variant="rounded" animation="wave" />
          </div>
        </div>
      );
    }

    const fare = fares[index];
    return (
      <div style={{...style, paddingRight: theme.spacing(1), boxSizing: 'border-box', display: 'flex', alignItems: 'center'}}>
        <HotelFareCard
          fare={fare}
          onSelectFare={onSelect}
          quotationStatus={quotationStatus}
        />
      </div>
    );
  };

  if (itemCount <= 3) {
    return (Array.from({ length: itemCount }).map((_, index) => (
      <div
        key={loading ? `skeleton-${index}` : fares[index]?.id}
        style = {{padding: theme.spacing(0.5), boxSizing: 'border-box'}}
      >
        {loading ? (
          <div style={{ height: '276px' }}>
            <Skeleton height="100%" variant="rounded" animation="wave" />
          </div>
        ) : (
          <HotelFareCard
            fare={fares[index]}
            onSelectFare={onSelect}
          />
        )}
      </div>
    ))
    );
  }

  return (
    <List
      height={calculatedHeight}
      width="100%"
      itemCount={itemCount}
      itemSize={318}
      overscanCount={2}
      outerElementType={ScrollContainer}
    >
      {renderItem}
    </List>
  );
};

